/**
 * <MePlanLookPage> - Iter 9.40 Pass C retail-as-look branch.
 *
 * Style category doesn't take appointments. Instead, the user "saves a look":
 * sizes + fit + vibe + inspo images + notes → writes to db.looks.
 *
 * Backend: POST /api/me/plan/look/save
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIBE_TAGS = ["streetwear", "minimal", "maximalist", "romcore", "quiet luxury", "utility", "y2k", "preppy", "clean girl"];
const SIZE_KEYS = [
  { key: "top", label: "Top" },
  { key: "bottom", label: "Bottom" },
  { key: "dress", label: "Dress" },
  { key: "shoe", label: "Shoe" },
];

export default function MePlanLookPage() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [sizes, setSizes] = useState({});
  const [vibe, setVibe] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleVibe = (v) => {
    setVibe((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]));
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/me/plan/look/save`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name || "My look",
          look_type: "style_look",
          category_id: "style",
          sizes,
          vibe,
          notes,
        }),
      });
      if (res.ok) {
        toast.success("Look saved");
        navigate("/me");
      } else {
        toast.error("Could not save look");
      }
    } catch {
      toast.error("Network error");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="me-plan-look-page">
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/me/plan")} className="p-2 -ml-2 hover:bg-gray-100 rounded-full" data-testid="me-look-back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-semibold text-base">Save a look</h1>
            <p className="text-xs text-gray-600">Fit + vibe + inspo - no booking, just your style.</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <Card>
          <CardContent className="p-4 space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Name</label>
              <Input
                placeholder="e.g. Summer 2026 capsule"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="me-look-name"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Sizes + fit</label>
              <div className="grid grid-cols-2 gap-2">
                {SIZE_KEYS.map(({ key, label }) => (
                  <Input
                    key={key}
                    placeholder={label}
                    value={sizes[key] || ""}
                    onChange={(e) => setSizes({ ...sizes, [key]: e.target.value })}
                    data-testid={`me-look-size-${key}`}
                  />
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Vibe</label>
              <div className="flex flex-wrap gap-2">
                {VIBE_TAGS.map((v) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => toggleVibe(v)}
                    data-testid={`me-look-vibe-${v.replace(/\s/g, "-")}`}
                    className="transition-colors"
                  >
                    <Badge
                      variant={vibe.includes(v) ? "default" : "secondary"}
                      className={`text-xs capitalize cursor-pointer ${vibe.includes(v) ? "bg-gray-900 text-white" : ""}`}
                    >
                      {v}
                    </Badge>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Notes</label>
              <Textarea
                rows={3}
                placeholder="Anything your stylist should know..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="me-look-notes"
              />
            </div>

            <Button
              onClick={save}
              disabled={saving}
              className="w-full bg-black hover:bg-gray-800"
              data-testid="me-look-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save look
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
